import React from "react";

import { useFilters, usePagination, useRowSelect, useSortBy, useTable } from "react-table";

import { Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import { HeaderSort } from "./HeaderSort";

import "./style.css";

type SortBy =
  | "views"
  | "form_submits"
  | "leads"
  | "total_orders"
  | "total_amount"
  | "ad_impressions"
  | "ad_spend";

type SortType = "ASC" | "DESC";

export type Sort = {
  sortBy?: SortBy;
  sortType?: SortType;
};

const ReportsTable = ({ columns, data, getHeaderProps, sort, setSort, onRowClick }: Props) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const handleSort = (disabled: boolean, id: string) => {
    if (disabled) return;
    if (sort.sortBy === id) {
      setSort({ ...sort, sortType: sort.sortType === "DESC" ? "ASC" : "DESC" });
      return;
    }
    setSort({ ...sort, sortBy: id as SortBy, sortType: "DESC" });
  };

  return (
    <Stack
      className="wbr-convertion-table"
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}>
      <Table
        size="small"
        sx={{
          borderCollapse: "collapse",
          tableLayout: "auto",
          width: "max-content",
          minWidth: "100%",
        }}
        {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup: any) => (
            <TableRow className="header-styling" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableCell
                  {...column.getHeaderProps([
                    { className: column.className },
                    getHeaderProps(column),
                  ])}
                  onClick={() => handleSort(column.disableSortBy, column.id)}
                  sx={{
                    width: column.width,
                    maxWidth: column.width,
                    whiteSpace: "nowrap",
                    ...(column.className === "wbr-ads-col-check"
                      ? { px: 0, pl: 1.25, pr: 0.25, width: 36, maxWidth: 36 }
                      : {}),
                    ...(column.className === "wbr-ads-col-status"
                      ? { px: 0, pl: 0.5, pr: 1, width: 110, maxWidth: 120 }
                      : {}),
                  }}>
                  <HeaderSort
                    column={column}
                    sortedAsc={column.id === sort.sortBy && sort.sortType === "ASC" && true}
                    sordedDesc={column.id === sort.sortBy && sort.sortType === "DESC" && true}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row: any) => {
            prepareRow(row);
            return (
              <TableRow
                className="table-row"
                hover={Boolean(onRowClick)}
                {...row.getRowProps(
                  onRowClick
                    ? {
                        onClick: () => onRowClick(row.original),
                        style: { cursor: "pointer" },
                      }
                    : {}
                )}>
                {row.cells.map((cell: any) => (
                  <TableCell
                    {...cell.getCellProps([{ className: cell.column.className }])}
                    sx={{
                      px: cell.column.className?.includes("wbr-ads-col-") ? 0.75 : 2,
                      py: 1.25,
                      fontSize: "0.75rem",
                      lineHeight: 1.45,
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      width: cell.column.width,
                      maxWidth: cell.column.width,
                      ...(cell.column.className === "wbr-ads-col-check"
                        ? { pl: 1.25, pr: 0.25, width: 36, maxWidth: 36 }
                        : {}),
                      ...(cell.column.className === "wbr-ads-col-status"
                        ? { pl: 0.5, pr: 1, width: 110, maxWidth: 120 }
                        : {}),
                    }}>
                    {cell.render("Cell")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default ReportsTable;

export interface Props {
  columns: any;
  data: any;
  getHeaderProps: any;
  sort: Sort;
  setSort: (sort: Sort) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
}
